import React from "react";
import { useTranslation } from "react-i18next";
import { Crown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  TEAM_ICON_SIZE,
  TEAM_PODIUM_AVATAR_SIZE,
  TEAM_PODIUM_CARD_GAP,
  TEAM_PODIUM_CARD_PADDING,
  TEAM_PODIUM_CARD_RADIUS,
  TEAM_PODIUM_NAME_COLOR,
  TEAM_PODIUM_RANK_BADGE_SIZE,
  TEAM_PODIUM_RANK_STYLES,
  TEAM_PODIUM_WINNER_BADGE_COLOR,
  TEAM_PODIUM_WINNER_CROWN_COLOR,
  TEAM_PODIUM_WINNER_NAME_BADGE_GAP,
} from "./constants";
import type { TeamSegment, TeamStaffRow } from "./types";

interface TeamStaffProfileCardProps {
  staff: TeamStaffRow;
  segment: TeamSegment;
  className?: string;
}

export const TeamStaffProfileCard: React.FC<TeamStaffProfileCardProps> = ({
  staff,
  segment,
  className,
}) => {
  const { t } = useTranslation();
  const rank = Math.min(staff.rank, 3) as 1 | 2 | 3;
  const styles = TEAM_PODIUM_RANK_STYLES[rank];
  const isWinner = staff.rank === 1;
  const showAvatar = segment === "staff";
  const winnerBadgeLabel =
    segment === "staff"
      ? t("analytics.team.employeeOfMonth", "Employee of the month")
      : t("analytics.team.teamOfMonth", "Team of the month");

  return (
    <article
      className={cn(
        "mx-auto flex w-full max-w-md flex-col border-2",
        className,
      )}
      style={{
        gap: TEAM_PODIUM_CARD_GAP,
        padding: TEAM_PODIUM_CARD_PADDING,
        borderRadius: TEAM_PODIUM_CARD_RADIUS,
        backgroundColor: styles.background,
        borderColor: styles.border,
      }}
    >
      <div
        className="flex flex-col items-center"
        style={{ gap: TEAM_PODIUM_CARD_GAP }}
      >
        <span
          className="inline-flex items-center justify-center rounded-full text-lg font-semibold leading-none"
          style={{
            width: TEAM_PODIUM_RANK_BADGE_SIZE,
            height: TEAM_PODIUM_RANK_BADGE_SIZE,
            backgroundColor: styles.circleBg,
            color: styles.circleText,
          }}
        >
          {staff.rank}
        </span>

        {showAvatar ? (
          <Avatar
            className="shrink-0 rounded-2xl"
            style={{
              width: TEAM_PODIUM_AVATAR_SIZE,
              height: TEAM_PODIUM_AVATAR_SIZE,
            }}
          >
            {staff.photoUrl ? (
              <AvatarImage src={staff.photoUrl} alt={staff.name} />
            ) : null}
            <AvatarFallback className="rounded-2xl text-base font-medium">
              {staff.initials}
            </AvatarFallback>
          </Avatar>
        ) : null}

        <div
          className="flex flex-col items-center text-center"
          style={{
            gap: isWinner
              ? TEAM_PODIUM_WINNER_NAME_BADGE_GAP
              : TEAM_PODIUM_CARD_GAP,
          }}
        >
          <span
            className="text-center text-lg font-medium leading-none"
            style={{ color: TEAM_PODIUM_NAME_COLOR }}
          >
            {staff.name}
          </span>
          {isWinner ? (
            <span
              className="inline-flex items-center justify-center gap-1 text-center text-lg font-medium leading-none"
              style={{ color: TEAM_PODIUM_WINNER_BADGE_COLOR }}
            >
              <Crown
                className="shrink-0"
                style={{
                  color: TEAM_PODIUM_WINNER_CROWN_COLOR,
                  width: TEAM_ICON_SIZE,
                  height: TEAM_ICON_SIZE,
                }}
              />
              {winnerBadgeLabel}
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
};
